import slugify from 'react-slugify';

import { useBodyTypesFilter } from '@/hooks/filters/body-types-filter.hook';
import { useBrandsRangesFilter } from '@/hooks/filters/brands-ranges-filter.hook';
import { useColorsFilter } from '@/hooks/filters/colors-filter.hook';
import { useDoorsFilter } from '@/hooks/filters/doors-filter.hook';
import { useDrivetrainFilter } from '@/hooks/filters/drivetrain-filter.hook';
import { useFeaturesFilter } from '@/hooks/filters/features-filter.hook';
import { useFuelConsumptionFilter } from '@/hooks/filters/fuel-consumption.hook';
import { useFuelTypeFilter } from '@/hooks/filters/fuel-type-filter.hook';
import { useMileageFilter } from '@/hooks/filters/mileage-filter.hook';
import { usePriceFilter } from '@/hooks/filters/price-filter.hook';
import { useTransmissionTypeFilter } from '@/hooks/filters/transmission-type-filter.hook';
import { useYearsFilterHook } from '@/hooks/filters/years-filter.hook';
import { Vehicle } from '@/types/used-cars/used-cars.types';

export const useQueryParams = () => {
  const { setSelectedPriceFilter, setPriceFilter } = usePriceFilter();
  const { setSelectedYearsFilter, setYearsFilter } = useYearsFilterHook();
  const { setSelectedMileageFilter, setMileageFilter } = useMileageFilter();
  const { setSelectedConsumptionFilter, setConsumptionFilter } =
    useFuelConsumptionFilter();
  const { selectBrand, selectRange } = useBrandsRangesFilter();
  const { selectBodyTypeFilter } = useBodyTypesFilter();
  const { selectColorFilter } = useColorsFilter();
  const { selectDoorsFilter } = useDoorsFilter();
  const { selectFeatures } = useFeaturesFilter();
  const { selectFuelType } = useFuelTypeFilter();
  const { selectTransmissionType } = useTransmissionTypeFilter();
  const { selectDrivetrainType } = useDrivetrainFilter();

  const selectFiltersFromQueryParams = (query: any) => {
    for (const key in query) {
      switch (key) {
        case '_or': {
          for (const orValue of query[key]) {
            for (const val of orValue._and) {
              if (val.brand_id) {
                selectBrand(val.brand_id.id._in);
              } else if (val.range_id) {
                selectRange(val.range_id.id._in);
              }
            }
          }

          break;
        }
        case '_and': {
          const featuresIds = [];

          for (const andValue of query[key]) {
            if (andValue?.spec_features) {
              featuresIds.push(andValue.spec_features?.spec_features_id?._eq);
            }
          }

          if (featuresIds?.length) {
            selectFeatures(featuresIds as any);
          }

          break;
        }
        case 'brand_id':
          selectBrand(query[key].id._in);
          break;
        case 'range_id':
          selectRange(query[key].id._in);
          break;
        case 'sales_price_including_vat': {
          const price = (query[key]._between as string)
            .split(',')
            .map((item) => Number(item));

          setPriceFilter({
            min: price[0] || 0,
            max: price[1] || 10000000000,
          });
          setSelectedPriceFilter({
            min: price[0] || 0,
            max: price[1] || 10000000000,
          });
          break;
        }
        case 'spec_body_type':
          selectBodyTypeFilter(query[key].id._in);
          break;
        case 'spec_body_colors':
          selectColorFilter(query[key].id._in);
          break;
        case 'spec_doors':
          selectDoorsFilter(query[key].id._in);
          break;
        case 'year': {
          const years = query[key]._in?.map((item: any) => Number(item));

          setYearsFilter({
            min: years[0],
            max: years[years.length - 1],
          });
          setSelectedYearsFilter({
            min: years[0],
            max: years[years.length - 1],
          });
          break;
        }
        case 'mileage': {
          const mileage = (query[key]._between as string)
            .split(',')
            .map((item) => Number(item));

          setMileageFilter({
            min: mileage[0] || 0,
            max: mileage[1] || 10000000000,
          });
          setSelectedMileageFilter({
            min: mileage[0] || 0,
            max: mileage[1] || 10000000000,
          });
          break;
        }
        case 'spec_fuel_type':
          selectFuelType(query[key].id._in);
          break;
        case 'spec_transmission':
          selectTransmissionType(query[key].id._in);
          break;
        case 'spec_drivetrain':
          selectDrivetrainType(query[key].id._in);
          break;
        case 'spec_litres_100km': {
          const litres =
            (query[key]?.float_value?._between as string)
              ?.split(',')
              ?.map((item) => Number(item)) ?? [];

          setConsumptionFilter({
            min: litres[0] ?? 0,
            max: litres[1] ?? 0,
          });
          setSelectedConsumptionFilter({
            min: litres[0] ?? 0,
            max: litres[1] ?? 0,
          });
          break;
        }
      }
    }
  };

  return { selectFiltersFromQueryParams };
};

export const getVehicleIdPath = (vehicle: Vehicle) =>
  `/used-cars/${slugify(vehicle.brand)}/${slugify(
    vehicle.range_id,
  )}/${vehicle.id}`;
